import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Document, Page } from "react-pdf";
import exportFromBlob from "helpers/export-from-blob";

class ResponsivePdfViewer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            numPages: null,
            pageNumber: 1,
            file: this.createFile(this.props.pdfData),
            containerWidth: null
        };

        this.containerRef = React.createRef();
        this.handleChangePage = this.handleChangePage.bind(this);
        this.handleSaveFile = this.handleSaveFile.bind(this);
        this.onDocumentLoadSuccess = this.onDocumentLoadSuccess.bind(this);
        this.updateContainerWidth = this.updateContainerWidth.bind(this);
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this.updateContainerWidth();
        window.addEventListener('resize', this.updateContainerWidth);
    }

    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('resize', this.updateContainerWidth);
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.pdfData, this.props.pdfData)) {
            this.setState({
                file: this.createFile(this.props.pdfData)
            });
        }
    }

    updateContainerWidth() {
        if (this._isMounted && this.containerRef.current) {
            const containerWidth = this.containerRef.current.offsetWidth;
            // Use 90% of container width with max of 1200px for readability
            const pageWidth = Math.min(containerWidth * 0.9, 1200);
            this.setState({ containerWidth: pageWidth });
        }
    }

    handleChangePage = (ev) => {
        const newPageNumber = parseInt(ev.target.value, 10);
        console.log('handleChangePage called:', { newPageNumber, currentPage: this.state.pageNumber });
        if (!isNaN(newPageNumber)) {
            const clampedPage = Math.max(1, Math.min(newPageNumber, this.state.numPages));
            console.log('Setting page to:', clampedPage);
            this.setState({
                pageNumber: clampedPage
            }, () => {
                console.log('Page changed to:', this.state.pageNumber);
                // Update controls after state change
                setTimeout(() => this.notifyControlsReady(), 50);
            });
        }
    };

    handleInputKeyDown = (ev) => {
        // Handle up/down arrow keys
        if (ev.key === 'ArrowUp') {
            ev.preventDefault();
            this.goToNextPage();
        } else if (ev.key === 'ArrowDown') {
            ev.preventDefault();
            this.goToPreviousPage();
        }
    };

    handleSaveFile = () => {
        exportFromBlob(this.props.pdfData, this.props.name || "file.pdf");
    };

    createFile = (pdfData) => {
        const file = new Blob([pdfData], {
            type: "application/pdf"
        });
        return URL.createObjectURL(file);
    };

    onDocumentLoadSuccess = ({ numPages }) => {
        if (!this._isMounted) return;
        
        this.setState({ numPages });
        // Update container width after document loads
        setTimeout(() => {
            if (this._isMounted) {
                this.updateContainerWidth();
            }
        }, 100);
        // Notify parent of controls after state is set
        setTimeout(() => {
            if (this._isMounted && this.props.onControlsReady) {
                this.notifyControlsReady();
            }
        }, 150);
    };

    notifyControlsReady = () => {
        if (this.props.onControlsReady) {
            this.props.onControlsReady({
                pagerComponent: this.getPagerComponent(),
                exportButton: this.getExportButton()
            });
        }
    };

    goToPreviousPage = () => {
        console.log('goToPreviousPage called:', { currentPage: this.state.pageNumber, canGoPrevious: this.state.pageNumber > 1 });
        if (this.state.pageNumber > 1) {
            const newPage = this.state.pageNumber - 1;
            console.log('Going to previous page:', newPage);
            this.setState({
                pageNumber: newPage
            }, () => {
                console.log('Previous page set to:', this.state.pageNumber);
                // Update controls after state change
                setTimeout(() => this.notifyControlsReady(), 50);
            });
        }
    };

    goToNextPage = () => {
        console.log('goToNextPage called:', { currentPage: this.state.pageNumber, numPages: this.state.numPages, canGoNext: this.state.pageNumber < this.state.numPages });
        if (this.state.pageNumber < this.state.numPages) {
            const newPage = this.state.pageNumber + 1;
            console.log('Going to next page:', newPage);
            this.setState({
                pageNumber: newPage
            }, () => {
                console.log('Next page set to:', this.state.pageNumber);
                // Update controls after state change
                setTimeout(() => this.notifyControlsReady(), 50);
            });
        }
    };

    getPagerComponent = () => {
        const {
            pageNumber,
            numPages
        } = this.state;
        return (
            <div className="d-flex align-items-center">
                <button 
                    className="btn btn-sm btn-outline-secondary mr-2"
                    onClick={this.goToPreviousPage}
                    disabled={pageNumber <= 1}
                    title="Previous page"
                >
                    ‹
                </button>
                <span className="mr-2">Page</span>
                <input
                    type="number"
                    value={pageNumber}
                    className="form-control form-control-sm mr-2"
                    onChange={this.handleChangePage}
                    onKeyDown={this.handleInputKeyDown}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur(); // Remove focus to trigger change
                        }
                    }}
                    style={{ width: '60px' }}
                    min="1"
                    max={numPages}
                />
                <span className="mr-2">of {numPages}</span>
                <button 
                    className="btn btn-sm btn-outline-secondary"
                    onClick={this.goToNextPage}
                    disabled={pageNumber >= numPages}
                    title="Next page"
                >
                    ›
                </button>
            </div>
        );
    };

    getExportButton = () => {
        return (
            <button
                className="btn btn-success btn-sm"
                onClick={this.handleSaveFile}
            >
                Export
            </button>
        );
    };

    render() {
        const {
            file,
            pageNumber,
            containerWidth
        } = this.state;

        return (
            <div
                ref={this.containerRef}
                className="position-relative"
                style={{
                    overflow: "hidden",
                    width: "100%"
                }}
            >
                <div className="text-center">
                    <Document
                        file={file}
                        onLoadSuccess={this.onDocumentLoadSuccess}
                        className="d-flex justify-content-center w-auto"
                    >
                        <Page
                            width={containerWidth || 800}
                            pageNumber={pageNumber}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />
                    </Document>
                </div>
            </div>
        );
    }
}

ResponsivePdfViewer.propTypes = {
    pdfData: PropTypes.any.isRequired,
    name: PropTypes.string,
    onControlsReady: PropTypes.func
};

export default ResponsivePdfViewer;
